import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Coins } from "lucide-react";

const GOLD_IMG =
  "https://images.unsplash.com/photo-1624365168056-daf44387e2ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYnVsbGlvbiUyMGJhciUyMGx1eHVyeSUyMGZpbmFuY2V8ZW58MHx8fHwxNzg5NjY1NDExfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#0B0E14]">
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-[480px] lg:flex-none">
        <div className="mx-auto w-full max-w-sm fade-up">
          <div className="mb-10 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[#F0B90B]/30 bg-[#F0B90B]/10">
              <Coins className="h-5 w-5 text-[#F0B90B]" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-widest text-[#EAECEF]">
                AUREUS <span className="text-[#F0B90B]">GOLD FUND</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5E6673]">XAU/USD Managed Trading</div>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#EAECEF]">Client Terminal Login</h1>
          <p className="mt-2 text-sm text-[#848E9C]">Access your managed gold trading account.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            {error && (
              <div data-testid="login-error-message" className="rounded-md border border-[#F6465D]/40 bg-[#F6465D]/10 px-3 py-2 text-sm text-[#F6465D]">
                {error}
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-[#848E9C]">Email or Account Number</label>
              <input
                data-testid="login-email-input"
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com or account number"
                className="w-full rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2.5 text-sm text-[#EAECEF] placeholder:text-[#5E6673] focus:border-[#F0B90B]/50 focus:outline-none"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-[#848E9C]">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2.5 text-sm text-[#EAECEF] placeholder:text-[#5E6673] focus:border-[#F0B90B]/50 focus:outline-none"
              />
            </div>
            <button
              data-testid="login-form-submit-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-[#F0B90B] py-2.5 text-sm font-semibold text-black shadow-[0_0_12px_rgba(240,185,11,0.25)] transition-colors hover:bg-[#F8D12F] disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
            <div className="text-center">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-[#848E9C] transition-colors hover:text-[#F0B90B]">
                Forgot your password?
              </Link>
            </div>
          </form>
        </div>
      </div>
      <div className="relative hidden flex-1 lg:block">
        <img src={GOLD_IMG} alt="Gold bullion" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14] via-[#0B0E14]/60 to-[#0B0E14]/20" />
        <div className="absolute bottom-16 left-12 max-w-md fade-up">
          <div className="mb-2 text-[10px] uppercase tracking-[0.3em] text-[#F0B90B]">Institutional Grade</div>
          <h2 className="font-heading text-4xl font-bold leading-tight tracking-tight text-[#EAECEF]">
            Your capital,<br />traded in gold.
          </h2>
          <p className="mt-3 text-sm text-[#848E9C]">
            Professional XAU/USD fund management with transparent trade reporting.
          </p>
        </div>
      </div>
    </div>
  );
}
